import React, { useEffect, useState } from "react";
import { api, apiErr } from "../../lib/api";
import { formatNum } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input, Label } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, Globe2, Leaf, Trash2, Pencil, Plus, Sun, TrendingDown, SlidersHorizontal } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Cell, Legend,
} from "recharts";

export function Scope2Tab({ d, re100, canEdit, onSaved }) {
  const [mktOpen, setMktOpen] = useState(false);
  const [re100Open, setRe100Open] = useState(false);
  if (!d) return <div className="card-flat p-6 text-sm text-muted-foreground" data-testid="ghg-scope2-loading">Loading Scope 2…</div>;
  const cards = [
    { key: "lb", label: "Location-based", val: d.location_based_tonnes, desc: "Grid-average factor for the region", cls: "" },
    { key: "mb", label: "Market-based", val: d.market_based_tonnes, desc: "After contractual instruments (RECs / green tariff)", cls: "text-accent" },
    { key: "gap", label: "Method gap", val: d.method_gap_tonnes, desc: `Instruments avoid ${d.renewable_pct}% of grid load`, cls: "text-[#00E65B]" },
  ];
  return (
    <div className="space-y-4" data-testid="ghg-scope2-tab">
      <div className="grid md:grid-cols-3 gap-3">
        {cards.map((c) => (
          <div key={c.key} className="card-flat p-4" data-testid={`ghg-scope2-${c.key}`}>
            <div className="label-eyebrow text-[10px]">{c.label}</div>
            <div className={`font-mono text-3xl font-bold ${c.cls}`} data-testid={`ghg-scope2-${c.key}-value`}>
              {formatNum(c.val, { decimals: 2 })} <span className="text-xs text-muted-foreground font-sans">tCO₂e</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">{c.desc}</p>
          </div>
        ))}
      </div>

      <Re100Card re100={re100} canEdit={canEdit} onEdit={() => setRe100Open(true)} onSaved={onSaved} />

      <div className="card-flat p-4" data-testid="ghg-scope2-regions">
        <div className="label-eyebrow text-[10px] flex items-center gap-1.5 mb-3"><Globe2 className="h-3.5 w-3.5 text-accent" /> Grid regions — tagged from the shared location reference</div>
        <div className="space-y-2">
          {(d.grid_regions || []).map((r) => (
            <div key={r.region} className="flex items-center justify-between gap-3 text-xs border-b border-border pb-2 last:border-0" data-testid={`ghg-scope2-region-${r.country}`}>
              <div><div className="font-medium">{r.region}</div><div className="text-[10px] text-muted-foreground font-mono">grid factor {r.grid_factor} kgCO₂e/kWh · {formatNum(r.kwh, { decimals: 0 })} kWh · {r.records} rec</div></div>
              <div className="text-right font-mono"><span className="text-muted-foreground">LB </span>{formatNum(r.location_based, { decimals: 2 })}<span className="text-accent ml-2">MB </span>{formatNum(r.market_based, { decimals: 2 })}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="card-flat p-4" data-testid="ghg-scope2-instruments">
        <div className="flex items-center justify-between mb-2">
          <span className="label-eyebrow text-[10px] flex items-center gap-1.5"><Leaf className="h-3.5 w-3.5 text-[#00E65B]" /> Market instruments · {d.renewable_pct}% renewable coverage</span>
          {canEdit && <Button size="sm" variant="outline" onClick={() => setMktOpen(true)} data-testid="ghg-scope2-edit-btn"><Pencil className="h-3 w-3" /> Edit</Button>}
        </div>
        {(d.instruments || []).length ? (d.instruments).map((i, idx) => (
          <div key={idx} className="text-xs flex items-center justify-between border-b border-border py-1.5 last:border-0" data-testid={`ghg-scope2-instrument-${idx}`}>
            <span>{i.type} <span className="text-muted-foreground">· {i.evidence}</span></span>
            <span className="font-mono">{i.coverage_pct}% · {i.factor_kgco2e} kgCO₂e/kWh</span>
          </div>
        )) : <div className="text-xs text-muted-foreground">No contractual instruments — market-based equals location-based.</div>}
      </div>

      <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5" data-testid="ghg-scope2-recon">
        <CheckCircle2 className="h-3.5 w-3.5 text-[#00E65B]" /> {d.reconciliation?.note}
      </div>

      <MarketInstrumentsDialog open={mktOpen} onOpenChange={setMktOpen} scope2={d} onSaved={() => { setMktOpen(false); onSaved(); }} />
      <Re100Dialog open={re100Open} onOpenChange={setRe100Open} re100={re100} onSaved={() => { setRe100Open(false); onSaved(); }} />
    </div>
  );
}

function Re100Card({ re100, canEdit, onEdit, onSaved }) {
  if (!re100) return null;
  const pts = re100.trajectory || [];
  return (
    <div className="card-flat p-4" data-testid="ghg-re100-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Sun className="h-4 w-4 text-[#FFB800]" />
          <span className="font-display font-semibold text-sm">RE100 progress — market-based shrinks as renewable coverage grows</span>
        </div>
        {canEdit && <Button size="sm" variant="outline" onClick={onEdit} data-testid="ghg-re100-edit-btn"><Pencil className="h-3 w-3" /> Adjust plan</Button>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat label="Renewable today" value={`${re100.current_coverage_pct}%`} testid="ghg-re100-current" />
        <Stat label="RE100 target" value={`${re100.target_pct}% · ${re100.target_year}`} testid="ghg-re100-target" />
        <Stat label="Avoided this year" value={`${formatNum(re100.current_avoided_tonnes, { decimals: 1 })} t`} cls="text-[#00E65B]" icon={<TrendingDown className="h-3 w-3" />} testid="ghg-re100-avoided" />
        <Stat label="Market-based at RE100" value={`${formatNum(re100.at_target_market_based, { decimals: 1 })} t`} cls="text-accent" testid="ghg-re100-attarget" />
      </div>

      <div style={{ width: "100%", height: 240 }} data-testid="ghg-re100-chart">
        <ResponsiveContainer>
          <ComposedChart data={pts} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" vertical={false} />
            <XAxis dataKey="year" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
            <YAxis yAxisId="l" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={40} />
            <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={34} unit="%" />
            <Tooltip
              contentStyle={{ background: "var(--surface, #0b1220)", border: "1px solid rgba(148,163,184,0.2)", borderRadius: 8, fontSize: 12 }}
              formatter={(v, n) => n === "Renewable %" ? [`${v}%`, n] : [`${formatNum(v, { decimals: 2 })} tCO₂e`, n]} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="l" dataKey="market_based" name="Market-based tCO₂e" radius={[4, 4, 0, 0]} maxBarSize={40}>
              {pts.map((p, i) => <Cell key={i} fill={p.actual ? "#0d9488" : "#5eead4"} />)}
            </Bar>
            <Line yAxisId="r" type="monotone" dataKey="coverage_pct" name="Renewable %" stroke="#00E65B" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[10px] text-muted-foreground mt-2">{re100.note} Solid bars = actuals, light bars = planned milestones.</p>

      <SolarWhatIf re100={re100} canEdit={canEdit} onSaved={onSaved} />
    </div>
  );
}

function SolarWhatIf({ re100, canEdit, onSaved }) {
  const baseLB = re100.current_location_based || 0;
  const currentCov = re100.current_coverage_pct || 0;
  const currentYear = re100.current_year;
  const planYear = re100.target_year;
  const [cov, setCov] = useState(Math.round(currentCov));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setCov(Math.round(currentCov)); }, [currentCov]);

  const mbAt = baseLB * (1 - cov / 100);
  const mbToday = baseLB * (1 - currentCov / 100);
  const avoided = Math.max(0, mbToday - mbAt);

  // Reach the slider coverage next year, then keep the same annual step to 100%.
  let projYear = planYear;
  const step = cov - currentCov;
  if (cov >= 100) projYear = currentYear + 1;
  else if (step > 0) projYear = currentYear + 1 + Math.ceil((100 - cov) / step);
  const shift = planYear - projYear;
  const shiftTxt = cov <= currentCov ? "on current plan" : shift > 0 ? `${shift} yr earlier` : shift < 0 ? `${-shift} yr later` : "on current plan";

  const savePace = async () => {
    setSaving(true);
    try {
      const traj = [{ year: currentYear, coverage_pct: currentCov, actual: true }];
      let y = currentYear + 1, c = cov;
      while (c < 100 && y <= currentYear + 40) {
        traj.push({ year: y, coverage_pct: Math.min(100, Math.round(c)), actual: false });
        y += 1; c += (step > 0 ? step : 100);
      }
      if (traj[traj.length - 1].coverage_pct < 100) traj.push({ year: y, coverage_pct: 100, actual: false });
      await api.put("/ghg/re100", { committed: true, target_year: projYear, target_pct: 100, trajectory: traj });
      toast.success("RE100 pace saved"); onSaved && onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    setSaving(false);
  };

  return (
    <div className="mt-3 border-t border-border pt-3" data-testid="ghg-re100-whatif">
      <div className="flex items-center gap-2 mb-2">
        <SlidersHorizontal className="h-3.5 w-3.5 text-accent" />
        <span className="label-eyebrow text-[10px]">Solar / PPA what-if — drag coverage to see the impact</span>
      </div>
      <div className="flex items-center gap-3">
        <input type="range" min={0} max={100} value={cov} onChange={(e) => setCov(Number(e.target.value))}
          className="w-full accent-[#00E65B]" data-testid="ghg-whatif-slider" />
        <span className="font-mono text-sm w-14 text-right" data-testid="ghg-whatif-cov">{cov}%</span>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-3">
        <Stat label="Market-based at this coverage" value={`${formatNum(mbAt, { decimals: 1 })} t`} cls="text-accent" testid="ghg-whatif-mb" />
        <Stat label="Avoided vs today" value={`${formatNum(avoided, { decimals: 1 })} t`} cls="text-[#00E65B]" icon={<TrendingDown className="h-3 w-3" />} testid="ghg-whatif-avoided" />
        <Stat label="Projected RE100" value={`${projYear}`} cls="text-[#FFB800]" testid="ghg-whatif-year" />
      </div>
      <p className="text-[10px] text-muted-foreground mt-2" data-testid="ghg-whatif-shift">
        At this pace RE100 lands in <b>{projYear}</b> — {shiftTxt} (plan: {planYear}). Assumes coverage reaches {cov}% next year, then holds the same annual pace to 100%.
      </p>
      {canEdit && (
        <div className="flex justify-end mt-2">
          <Button size="sm" variant="outline" onClick={savePace} disabled={saving || cov <= currentCov} data-testid="ghg-whatif-save">
            {saving ? "Saving…" : "Save this pace as plan"}
          </Button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, cls = "", icon, testid }) {
  return (
    <div className="border border-border rounded-md p-2.5" data-testid={testid}>
      <div className="label-eyebrow text-[9px]">{label}</div>
      <div className={`font-mono text-lg font-bold inline-flex items-center gap-1 ${cls}`}>{icon}{value}</div>
    </div>
  );
}

function MarketInstrumentsDialog({ open, onOpenChange, scope2, onSaved }) {
  const [renewable, setRenewable] = useState(0);
  const [instruments, setInstruments] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open && scope2) { setRenewable(scope2.renewable_pct || 0); setInstruments(scope2.instruments || []); } }, [open, scope2]);

  const setInst = (i, k, v) => setInstruments((p) => p.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const addInst = () => setInstruments((p) => [...p, { type: "", coverage_pct: 0, factor_kgco2e: 0, evidence: "" }]);
  const rmInst = (i) => setInstruments((p) => p.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/ghg/scope2/config", {
        renewable_pct: Number(renewable),
        instruments: instruments.map((i) => ({ type: i.type, coverage_pct: Number(i.coverage_pct) || 0, factor_kgco2e: Number(i.factor_kgco2e) || 0, evidence: i.evidence || null })),
      });
      toast.success("Market instruments saved"); onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ghg-scope2-dialog">
        <DialogHeader><DialogTitle>Scope 2 market instruments</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Renewable coverage (% of grid load offset by RECs / green tariff)</Label>
            <Input type="number" value={renewable} onChange={(e) => setRenewable(e.target.value)} data-testid="ghg-scope2-renewable" /></div>
          <div className="space-y-2">
            <Label>Contractual instruments</Label>
            {instruments.map((i, idx) => (
              <div key={idx} className="border border-border rounded-md p-2 space-y-1.5" data-testid={`ghg-scope2-inst-row-${idx}`}>
                <Input placeholder="Type (e.g. Green tariff, PPA, REC)" value={i.type} onChange={(e) => setInst(idx, "type", e.target.value)} data-testid={`ghg-scope2-inst-type-${idx}`} />
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" placeholder="Coverage %" value={i.coverage_pct} onChange={(e) => setInst(idx, "coverage_pct", e.target.value)} data-testid={`ghg-scope2-inst-cov-${idx}`} />
                  <Input type="number" placeholder="Factor kgCO₂e/kWh" value={i.factor_kgco2e} onChange={(e) => setInst(idx, "factor_kgco2e", e.target.value)} />
                </div>
                <Input placeholder="Evidence" value={i.evidence || ""} onChange={(e) => setInst(idx, "evidence", e.target.value)} />
                <Button size="sm" variant="ghost" className="text-red-400 h-7" onClick={() => rmInst(idx)} data-testid={`ghg-scope2-inst-rm-${idx}`}><Trash2 className="h-3 w-3" /> Remove</Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addInst} data-testid="ghg-scope2-inst-add"><Plus className="h-3 w-3" /> Add instrument</Button>
          </div>
          <div className="flex justify-end"><Button variant="primary" onClick={save} disabled={saving} data-testid="ghg-scope2-config-save">{saving ? "Saving…" : "Save"}</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Re100Dialog({ open, onOpenChange, re100, onSaved }) {
  const [targetYear, setTargetYear] = useState(2030);
  const [targetPct, setTargetPct] = useState(100);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && re100) {
      setTargetYear(re100.target_year || 2030);
      setTargetPct(re100.target_pct || 100);
      setRows((re100.trajectory || []).map((p) => ({ year: p.year, coverage_pct: p.coverage_pct, actual: !!p.actual })));
    }
  }, [open, re100]);

  const setRow = (i, k, v) => setRows((p) => p.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const addRow = () => setRows((p) => [...p, { year: (p.length ? Math.max(...p.map((r) => Number(r.year))) + 1 : new Date().getFullYear()), coverage_pct: 0, actual: false }]);
  const rmRow = (i) => setRows((p) => p.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/ghg/re100", {
        committed: true, target_year: Number(targetYear), target_pct: Number(targetPct),
        trajectory: rows.map((r) => ({ year: Number(r.year), coverage_pct: Number(r.coverage_pct) || 0, actual: !!r.actual })),
      });
      toast.success("RE100 plan saved"); onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ghg-re100-dialog">
        <DialogHeader><DialogTitle>RE100 renewable-energy plan</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Target year</Label><Input type="number" value={targetYear} onChange={(e) => setTargetYear(e.target.value)} data-testid="ghg-re100-target-year" /></div>
            <div><Label>Target coverage %</Label><Input type="number" value={targetPct} onChange={(e) => setTargetPct(e.target.value)} data-testid="ghg-re100-target-pct" /></div>
          </div>
          <div className="space-y-1.5">
            <Label>Coverage trajectory (year → renewable %)</Label>
            {rows.map((r, idx) => (
              <div key={idx} className="flex items-center gap-2" data-testid={`ghg-re100-row-${idx}`}>
                <Input type="number" className="w-24" value={r.year} onChange={(e) => setRow(idx, "year", e.target.value)} />
                <Input type="number" className="w-24" value={r.coverage_pct} onChange={(e) => setRow(idx, "coverage_pct", e.target.value)} />
                <label className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <input type="checkbox" checked={!!r.actual} onChange={(e) => setRow(idx, "actual", e.target.checked)} /> actual
                </label>
                <Button size="sm" variant="ghost" className="text-red-400 h-7" onClick={() => rmRow(idx)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addRow} data-testid="ghg-re100-add-row"><Plus className="h-3 w-3" /> Add year</Button>
          </div>
          <div className="flex justify-end"><Button variant="primary" onClick={save} disabled={saving} data-testid="ghg-re100-save">{saving ? "Saving…" : "Save plan"}</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
